import Swiper from 'swiper';
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';
import GLightbox from 'glightbox';
import { createApp } from 'vue';
import NewsList from './components/NewsList.vue';

document.addEventListener("DOMContentLoaded", function (event) {

    // ── Navigation Dropdowns (Nav-Submenüs + Sprachwechsler) ─────────────────

    const HOVER_CLOSE_DELAY = 300;

    // Unified dropdown: hover (Maus) + Enter/Space auf Toggle (Tastatur)
    function setupDropdown(wrapper, toggleBtn, menu, openClass, onBeforeOpen, hoverEl) {
        const links = menu ? [...menu.querySelectorAll('a')] : [];
        let closeTimer = null;
        const trigger = hoverEl || wrapper;

        const open = (moveFocus = false) => {
            clearTimeout(closeTimer);
            onBeforeOpen?.();
            wrapper.classList.add(openClass);
            toggleBtn?.setAttribute('aria-expanded', 'true');
            links.forEach(a => a.removeAttribute('tabindex'));
            if (moveFocus && links[0]) links[0].focus();
        };

        const close = () => {
            clearTimeout(closeTimer);
            wrapper.classList.remove(openClass);
            toggleBtn?.setAttribute('aria-expanded', 'false');
            links.forEach(a => a.setAttribute('tabindex', '-1'));
        };

        wrapper._closeDropdown = close;

        trigger.addEventListener('mouseenter', () => open(false));
        trigger.addEventListener('mouseleave', () => {
            clearTimeout(closeTimer);
            closeTimer = setTimeout(close, HOVER_CLOSE_DELAY);
        });

        if (hoverEl && menu) {
            menu.addEventListener('mouseenter', () => clearTimeout(closeTimer));
            menu.addEventListener('mouseleave', () => {
                clearTimeout(closeTimer);
                closeTimer = setTimeout(close, HOVER_CLOSE_DELAY);
            });
        }

        toggleBtn?.addEventListener('keydown', (e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            wrapper.classList.contains(openClass)
                ? (close(), toggleBtn.focus())
                : open(true);
        });

        wrapper.addEventListener('focusout', (e) => {
            if (!wrapper.contains(e.relatedTarget)) close();
        });

        wrapper.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && wrapper.classList.contains(openClass)) {
                e.preventDefault();
                close();
                toggleBtn?.focus();
            }
        });
    }

    // Sprachwechsler
    const langWrapper = document.querySelector('.nav_menu--language');
    if (langWrapper) {
        setupDropdown(
            langWrapper,
            langWrapper.querySelector('button'),
            langWrapper.querySelector('#language_menu'),
            'nav_menu--language--open'
        );
    }

    // Nav-Submenüs (nur eines gleichzeitig offen, instant switch ohne Transition)
    const navDropdowns = [...document.querySelectorAll('.nav_item')]
        .filter(item => item.querySelector('.nav_submenu'));

    navDropdowns.forEach(item => {
        setupDropdown(
            item,
            item.querySelector('.nav_toggle'),
            item.querySelector('.nav_submenu'),
            'nav_item--open',
            () => {
                navDropdowns.forEach(other => {
                    if (other !== item && other.classList.contains('nav_item--open')) {
                        const prevMenu = other.querySelector('.nav_submenu');
                        if (prevMenu) prevMenu.style.transition = 'none';
                        other._closeDropdown?.();
                        if (prevMenu) requestAnimationFrame(() => { prevMenu.style.transition = ''; });
                    }
                });
            },
            item.querySelector('.nav_item__head')
        );
    });

    // MOBILE NAV TOGGLE
    document.querySelectorAll('.nav_toggle').forEach(toggle => {
        toggle.addEventListener('click', () => {
            const item = toggle.closest('.nav_item');
            const isOpen = item.classList.toggle('nav_item--mobile-open');
            toggle.setAttribute('aria-expanded', String(isOpen));
            if (!isOpen) item.classList.remove('nav_item--open');
        });
    });

    // Pre-open the submenu that contains the current active page
    document.querySelectorAll('.nav_item--has-children').forEach(item => {
        if (item.querySelector('.nav_sublink--active')) {
            item.classList.add('nav_item--mobile-open');
            const toggle = item.querySelector('.nav_toggle');
            if (toggle) toggle.setAttribute('aria-expanded', 'true');
        }
    });

    // INIT MENUBUTTON
    const menu_button = document.querySelector('[data-behaviour="toggle-menu"]');
    menu_button.addEventListener('click', () => {
        document.body.classList.toggle('body--show');
    })

    // INIT LIGHTBOX
    const lightbox = GLightbox({
        selector: '[data-gallery="gallery"]',
        touchNavigation: true,
        loop: true,
    });

    // INIT SWIPER
    const sliders = document.querySelectorAll('.swiper');
    sliders.forEach(function (slider) {
        const autoSwiper = slider.classList.contains('swiper--auto');
        const swiper = new Swiper(slider, {
            modules: [Pagination, Navigation, Autoplay, EffectFade],
            effect: 'slide',
            fadeEffect: {
                crossFade: true
            },
            direction: 'horizontal',
            loop: true,

            autoplay: autoSwiper ? {
                delay: 5000,
            } : false,

            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },

            pagination: {
                el: '.swiper-pagination',
                type: 'bullets',
                clickable: true,
            },
        });
    });

    function openEl(el, fromH = 0) {
        el.style.overflow = 'hidden';
        el.style.height = fromH + 'px';
        el.offsetHeight;
        el.style.height = el.scrollHeight + 'px';
        el.addEventListener('transitionend', () => { el.style.height = ''; el.style.overflow = ''; }, { once: true });
    }

    function closeEl(el, toH = 0, done) {
        el.style.overflow = 'hidden';
        el.style.height = el.scrollHeight + 'px';
        el.offsetHeight;
        el.style.height = toH + 'px';
        el.addEventListener('transitionend', () => { el.style.height = ''; el.style.overflow = ''; done?.(); }, { once: true });
    }

    // ACCORDION
    function animateDetails(details, open) {
        const content = details.querySelector('.accordion_entry_content');
        if (!content) { open ? details.setAttribute('open', '') : details.removeAttribute('open'); return; }
        if (open) { details.setAttribute('open', ''); openEl(content); }
        else closeEl(content, 0, () => details.removeAttribute('open'));
    }

    document.querySelectorAll('.section_accordion').forEach(accordion => {
        accordion.querySelectorAll('details.accordion_entry').forEach(details => {
            details.addEventListener('click', e => {
                if (!e.target.closest('summary')) return;
                e.preventDefault();
                const willOpen = !details.open;
                accordion.querySelectorAll('details[open]').forEach(open => {
                    if (open !== details) animateDetails(open, false);
                });
                animateDetails(details, willOpen);
            });
        });
    });

    // NEWS ACCORDION
    document.querySelectorAll('.NewsItem__Body').forEach(body => {
        const text = body.querySelector('.NewsItem__Body--text');
        const btn  = body.querySelector('.link__button--accordion');
        if (!text || !btn) return;

        text.classList.add('is-collapsed');
        const collapsedH = text.clientHeight;
        if (text.scrollHeight <= collapsedH) { text.classList.remove('is-collapsed'); return; }

        btn.classList.add('is-visible');
        const { labelMore, labelLess } = btn.dataset;

        btn.addEventListener('click', () => {
            const isExpanded = btn.getAttribute('aria-expanded') === 'true';
            if (isExpanded) {
                closeEl(text, collapsedH, () => text.classList.add('is-collapsed'));
            } else {
                text.classList.remove('is-collapsed');
                openEl(text, collapsedH);
            }
            btn.setAttribute('aria-expanded', String(!isExpanded));
            btn.textContent = isExpanded ? labelMore : labelLess;
        });
    });

    // BIRTHDAY POPUPS
    document.querySelectorAll('[data-birthday-open]').forEach(button => {
        button.addEventListener('click', () => {
            const dialog = document.getElementById('birthday-popup-' + button.dataset.birthdayOpen);
            if (dialog) dialog.showModal();
        });
    });

    document.querySelectorAll('.birthday_popup').forEach(dialog => {
        dialog.querySelector('.birthday_popup__close')?.addEventListener('click', () => dialog.close());
        dialog.addEventListener('click', e => { if (e.target === dialog) dialog.close(); });
    });

    // FIXED HEADER — hysteresis prevents flickering at the threshold
    window.addEventListener('scroll', () => {
        const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
        const isFixed = document.body.classList.contains('menu--fixed');
        if (!isFixed && scrollTop > 60) {
            document.body.classList.add('menu--fixed');
        } else if (isFixed && scrollTop < 20) {
            document.body.classList.remove('menu--fixed');
        }
    }, { passive: true });

    document.querySelectorAll('.section_title').forEach(title => {
        const length = title.textContent.trim().length;
        if (length > 50) {
            title.style.fontSize = '1.25rem';
        }
    });

    // Mount Vue news list (NewsPage only)
    const newsApp = document.getElementById('news-app');
    if (newsApp && window.__newsData__) {
        createApp(NewsList, {
            items:  window.__newsData__,
            locale: newsApp.dataset.locale || 'de_DE',
        }).mount(newsApp);
    }
});
